"use client";
import { useEffect, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import UsermanMenu from "../components/UsermanMenu";

const API_BASE = "https://api.wifikamu.my.id";

export default function UsermanUserGroup() {
  const [searchParams] = useSearchParams();
  const usermanId = searchParams.get("userman_id");
  const deviceName = searchParams.get("device_name");

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [form, setForm] = useState({
    id: null,
    userman_id: 1,
    name: "",
    outer_auths: "",
    inner_auths: "",
    attributes: "",
    is_default: false,
  });

  // 🔄 Fetch all groups
  const fetchGroups = async (query = "") => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/userman-user-group-get${query}`);
      const json = await res.json();
      if (json.success) {
        setGroups(json.data);
      } else {
        toast.error(json.message || "Gagal ambil data");
      }
    } catch {
      toast.error("Gagal konek ke server");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  // 🔍 Search
  const handleSearch = () => {
    if (!search) return fetchGroups();
    fetchGroups(`?name=${search}`);
  };

  // ➕ Add / Edit Modal
  const openForm = (group = null) => {
    if (group) {
      setForm({ ...group });
    } else {
      setForm({
        id: null,
        userman_id: 1,
        name: "",
        outer_auths: "",
        inner_auths: "",
        attributes: "",
        is_default: false,
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const method = form.id ? "PUT" : "POST";
      const endpoint = form.id
        ? `${API_BASE}/userman-user-group-update`
        : `${API_BASE}/userman-user-group-add`;

      const res = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const json = await res.json();
      if (json.success) {
        toast.success(
          form.id ? "Berhasil update group" : "Berhasil tambah group"
        );
        fetchGroups();
        setIsModalOpen(false);
      } else {
        toast.error(json.message || "Gagal simpan");
      }
    } catch {
      toast.error("Error saat simpan data");
    }
  };

  // ❌ Delete
  const confirmDelete = (group) => {
    setSelectedGroup(group);
    setIsConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_BASE}/userman-user-group-delete`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedGroup.id }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success("Berhasil hapus group");
        fetchGroups();
      } else {
        toast.error(json.message || "Gagal hapus");
      }
    } catch {
      toast.error("Error hapus data");
    }
    setIsConfirmOpen(false);
  };

  return (
    <div className="container mx-auto p-6">
      <Toaster position="top-right" />

      <h2 className="text-2xl font-semibold mb-4">
        {deviceName} /{" "}
        <UsermanMenu userman_id={usermanId} device_name={deviceName} /> / User
        Group
      </h2>

      {/* 🔍 Search */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Cari berdasarkan name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border px-3 py-2 rounded w-1/3 dark:bg-gray-900"
        />
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 text-white rounded"
        >
          Cari
        </button>
        <button
          onClick={() => openForm()}
          className="ml-auto px-4 py-2 bg-green-600 text-white rounded"
        >
          + Tambah
        </button>
      </div>

      {/* 📋 Table */}
      <div className="bg-white dark:bg-gray-900 shadow rounded-lg overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-100 dark:bg-gray-800">
            <tr>
              <th className="p-3">ID</th>
              <th className="p-3">Name</th>
              <th className="p-3">Outer Auths</th>
              <th className="p-3">Inner Auths</th>
              <th className="p-3">Attributes</th>
              <th className="p-3">Default?</th>
              <th className="p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="p-4 text-center">
                  Loading...
                </td>
              </tr>
            ) : groups.length > 0 ? (
              groups.map((g) => (
                <tr
                  key={g.id}
                  className="border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <td className="p-3">{g.id}</td>
                  <td className="p-3">{g.name}</td>
                  <td className="p-3">{g.outer_auths}</td>
                  <td className="p-3">{g.inner_auths}</td>
                  <td className="p-3">{g.attributes || "-"}</td>
                  <td className="p-3">{g.is_default ? "✅" : "❌"}</td>
                  <td className="p-3 flex justify-center gap-3">
                    <button
                      onClick={() => openForm(g)}
                      className="px-3 py-2 text-blue-600 hover:text-blue-800 border border-blue-600 hover:border-blue-800 rounded-md text-xs"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => confirmDelete(g)}
                      className="px-3 py-2 text-red-600 hover:text-red-800 border border-red-600 hover:border-red-800 rounded-md text-xs"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="p-4 text-center text-gray-500">
                  Tidak ada data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 📝 Modal Add/Edit */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-lg"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3 className="text-lg font-semibold mb-4">
                {form.id ? "Edit User Group" : "New User Group"}
              </h3>

              {/* 🧾 Field Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Name:</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-900"
                />
              </div>

              {/* 🌐 Outer Auths */}
              <div className="border-t border-b border-gray-300 dark:border-gray-700 py-3 mb-4">
                <h4 className="text-sm font-semibold mb-2">Outer Auths:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {[
                    "PAP",
                    "CHAP",
                    "MSCHAP1",
                    "MSCHAP2",
                    "EAP TLS",
                    "EAP TTLS",
                    "EAP PEAP",
                    "EAP MSCHAP2",
                  ].map((auth) => (
                    <label key={auth} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={form.outer_auths.split(",").includes(auth)}
                        onChange={(e) => {
                          let updated = form.outer_auths
                            ? form.outer_auths.split(",").filter((a) => a)
                            : [];
                          if (e.target.checked) {
                            updated.push(auth);
                          } else {
                            updated = updated.filter((a) => a !== auth);
                          }
                          setForm({ ...form, outer_auths: updated.join(",") });
                        }}
                      />
                      {auth}
                    </label>
                  ))}
                </div>
              </div>

              {/* 🔐 Inner Auths */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold mb-2">Inner Auths:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {[
                    "TTLS PAP",
                    "TTLS CHAP",
                    "TTLS MSCHAP1",
                    "TTLS MSCHAP2",
                    "PEAP MSCHAP2",
                  ].map((auth) => (
                    <label key={auth} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={form.inner_auths.split(",").includes(auth)}
                        onChange={(e) => {
                          let updated = form.inner_auths
                            ? form.inner_auths.split(",").filter((a) => a)
                            : [];
                          if (e.target.checked) {
                            updated.push(auth);
                          } else {
                            updated = updated.filter((a) => a !== auth);
                          }
                          setForm({ ...form, inner_auths: updated.join(",") });
                        }}
                      />
                      {auth}
                    </label>
                  ))}
                </div>
              </div>

              {/* ⚙️ Default Option */}
              <div className="mb-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_default}
                  onChange={(e) =>
                    setForm({ ...form, is_default: e.target.checked })
                  }
                />
                <span className="text-sm">Set as default</span>
              </div>

              {/* 🧭 Buttons */}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  OK
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ❌ Modal Delete */}
      <AnimatePresence>
        {isConfirmOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3 className="text-lg font-semibold mb-4">Konfirmasi Hapus</h3>
              <p>
                Yakin ingin hapus group <strong>{selectedGroup?.name}</strong>?
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setIsConfirmOpen(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded"
                >
                  Batal
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded"
                >
                  Hapus
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
